// Natural Language Routine Parser

import type { Contact } from '@/types/contact'
import type { MonthDayOfWeek, MonthWeek } from '@/types/actionable'

export interface SemanticToken {
  text: string
  type: 'person' | 'action' | 'day-pattern' | 'time-of-day' | 'time' | 'plain'
}

export interface ParsedRoutine {
  raw: string
  assignee: string | null       // Contact ID if matched, null if self
  assigneeName: string | null   // Display name
  action: string                // The core action text
  recurrence: {
    // 'weekend' is once, either day; 'weekends' is both days, because
    // "Saturday and Sunday" names two days and "every weekend" names one
    // window (Scott, 2026-09-20).
    type: 'daily' | 'weekdays' | 'weekend' | 'weekends' | 'weekly' | 'biweekly' | 'monthly' | 'quarterly' | 'yearly'
    days?: number[]             // 0=Sun, 1=Mon, etc. for weekly
    interval?: number           // e.g., 2 for "every other"
    dayOfMonth?: number         // For monthly: 1-31
    weekOfMonth?: MonthWeek     // For monthly by position: 1-4, -1 = last
    dayOfWeek?: MonthDayOfWeek  // For monthly by position: 'weekend' or 'sun'…'sat'
  }
  timeOfDay: 'morning' | 'afternoon' | 'evening' | null
  time: string | null           // HH:MM format
  tokens: SemanticToken[]
}

// Day name mappings
const DAY_NAMES: Record<string, number> = {
  sunday: 0, sun: 0,
  monday: 1, mon: 1,
  tuesday: 2, tue: 2, tues: 2,
  wednesday: 3, wed: 3,
  thursday: 4, thu: 4, thurs: 4,
  friday: 5, fri: 5,
  saturday: 6, sat: 6,
}

// Time of day keywords
const TIME_OF_DAY_KEYWORDS = ['morning', 'afternoon', 'evening']

/**
 * Parse a natural language routine description
 */
export function parseRoutine(input: string, contacts: Contact[] = []): ParsedRoutine {
  const raw = input.trim()
  const normalized = raw.toLowerCase()

  let assignee: string | null = null
  let assigneeName: string | null = null
  let action = ''
  let recurrence: ParsedRoutine['recurrence'] = { type: 'daily' }
  let timeOfDay: ParsedRoutine['timeOfDay'] = null
  let time: string | null = null
  const tokens: SemanticToken[] = []

  // Track what we've extracted to build the action string
  const extractedRanges: Array<{ start: number; end: number; type: SemanticToken['type']; text: string }> = []

  // 1. Extract time - MUCH more flexible patterns
  const timePatterns = [
    // Named times (with optional "at")
    { regex: /\b(?:at\s+)?noon\b/i, parse: () => '12:00' },
    { regex: /\b(?:at\s+)?midnight\b/i, parse: () => '00:00' },

    // Military time: 1930, 0700, 1400 (4 digits, NOT followed by am/pm)
    { regex: /\b(\d{4})\b(?!\s*(?:am?|pm?))/i, parse: (m: RegExpMatchArray) => {
      const num = m[1]
      const hours = parseInt(num.slice(0, 2), 10)
      const minutes = parseInt(num.slice(2), 10)
      if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
      }
      return null
    }},

    // Compact no-colon with meridiem: 700p, 700pm, 1130a, 1130am (with optional "at")
    { regex: /\b(?:at\s+)?(\d{3,4})\s*(am?|pm?)\b/i, parse: (m: RegExpMatchArray) => {
      const num = m[1]
      const meridiem = m[2].toLowerCase()
      let hours: number
      let minutes: number

      if (num.length === 3) {
        // 700 = 7:00
        hours = parseInt(num[0], 10)
        minutes = parseInt(num.slice(1), 10)
      } else {
        // 1130 = 11:30
        hours = parseInt(num.slice(0, 2), 10)
        minutes = parseInt(num.slice(2), 10)
      }

      if (minutes > 59) return null

      if (meridiem.startsWith('p')) {
        if (hours !== 12) hours += 12
      } else if (hours === 12) {
        hours = 0
      }

      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
    }},

    // Standard with colon and meridiem: 7:30pm, 7:30 pm, at 7:30pm (with optional "at")
    { regex: /\b(?:at\s+)?(\d{1,2}):(\d{2})\s*(am?|pm?)\b/i, parse: (m: RegExpMatchArray) => {
      let hours = parseInt(m[1], 10)
      const minutes = parseInt(m[2], 10)
      const meridiem = m[3].toLowerCase()
      if (meridiem.startsWith('p')) {
        if (hours !== 12) hours += 12
      } else if (hours === 12) {
        hours = 0
      }
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
    }},

    // Just hour with meridiem: 7pm, 7p, 7 pm, at 7pm (with optional "at")
    { regex: /\b(?:at\s+)?(\d{1,2})\s*(am?|pm?)\b/i, parse: (m: RegExpMatchArray) => {
      let hours = parseInt(m[1], 10)
      const meridiem = m[2].toLowerCase()
      if (meridiem.startsWith('p')) {
        if (hours !== 12) hours += 12
      } else if (hours === 12) {
        hours = 0
      }
      return `${hours.toString().padStart(2, '0')}:00`
    }},

    // "at 7" (requires "at" prefix - bare numbers without meridiem)
    { regex: /\bat\s+(\d{1,2})\b(?!\s*:|(?:\d)|(?:am?|pm?))/i, parse: (m: RegExpMatchArray) => {
      const hours = parseInt(m[1], 10)
      return `${hours.toString().padStart(2, '0')}:00`
    }},
  ]

  for (const { regex, parse } of timePatterns) {
    const match = normalized.match(regex)
    if (match && match.index !== undefined) {
      const parsedTime = parse(match)
      if (parsedTime !== null) {
        time = parsedTime
        extractedRanges.push({
          start: match.index,
          end: match.index + match[0].length,
          type: 'time',
          text: formatTimeDisplay(time),
        })
        break
      }
    }
  }

  // 2. Extract recurrence patterns
  const recurrencePatterns = [
    // Every other / alternate patterns (must come first to match before simpler patterns)
    { regex: /\bevery\s+other\s+day\b/i, result: { type: 'daily' as const, interval: 2 } },
    { regex: /\balternate\s+days?\b/i, result: { type: 'daily' as const, interval: 2 } },
    { regex: /\bevery\s+other\s+week\b/i, result: { type: 'biweekly' as const } },
    { regex: /\bbiweekly\b/i, result: { type: 'biweekly' as const } },
    { regex: /\bfortnightly\b/i, result: { type: 'biweekly' as const } },
    { regex: /\bevery\s+two\s+weeks?\b/i, result: { type: 'biweekly' as const } },
    { regex: /\bevery\s+2\s+weeks?\b/i, result: { type: 'biweekly' as const } },
    // Quarterly/Yearly
    { regex: /\bquarterly\b/i, result: { type: 'quarterly' as const } },
    { regex: /\bevery\s+quarter\b/i, result: { type: 'quarterly' as const } },
    { regex: /\bevery\s+3\s+months?\b/i, result: { type: 'quarterly' as const } },
    { regex: /\bevery\s+three\s+months?\b/i, result: { type: 'quarterly' as const } },
    { regex: /\byearly\b/i, result: { type: 'yearly' as const } },
    { regex: /\bannually\b/i, result: { type: 'yearly' as const } },
    { regex: /\bevery\s+year\b/i, result: { type: 'yearly' as const } },
    // Monthly (without day number - will be enhanced below)
    { regex: /\bmonthly\b/i, result: { type: 'monthly' as const } },
    { regex: /\bevery\s+month\b/i, result: { type: 'monthly' as const } },
    // Weekdays
    { regex: /\bevery\s+weekday\b/i, result: { type: 'weekdays' as const } },
    { regex: /\bweekdays\b/i, result: { type: 'weekdays' as const } },
    { regex: /\bmon(?:day)?\s*(?:-|through|to)\s*fri(?:day)?\b/i, result: { type: 'weekdays' as const } },
    { regex: /\bmonday\s+through\s+friday\b/i, result: { type: 'weekdays' as const } },
    // Weekends
    { regex: /\bevery\s+weekend\b/i, result: { type: 'weekend' as const } },
    { regex: /\bweekends\b/i, result: { type: 'weekend' as const } },
    { regex: /\bon\s+the\s+weekend\b/i, result: { type: 'weekend' as const } },
    { regex: /\bsaturday\s+and\s+sunday\b/i, result: { type: 'weekends' as const } },
    { regex: /\bsat(?:urday)?\s+and\s+sun(?:day)?\b/i, result: { type: 'weekends' as const } },
    // Daily
    { regex: /\bevery\s+day\b/i, result: { type: 'daily' as const } },
    { regex: /\bdaily\b/i, result: { type: 'daily' as const } },
    // "every morning/afternoon/evening" implies daily
    { regex: /\bevery\s+(?=morning|afternoon|evening)/i, result: { type: 'daily' as const } },
  ]

  let recurrenceFound = false

  // Monthly by position: "first weekend of the month", "monthly on the last
  // friday", "the 2nd tuesday of every month". Checked first — otherwise the
  // bare weekday falls through to a weekly rule ("first saturday" → every
  // Saturday), and "every month" to a monthly one with no day at all.
  {
    const ORD = '(first|1st|second|2nd|third|3rd|fourth|4th|last)'
    const DOW = '(weekend|sun(?:day)?|mon(?:day)?|tues?(?:day)?|wed(?:nes)?(?:day)?|thurs?(?:day)?|fri(?:day)?|sat(?:urday)?)'
    const positionPatterns = [
      new RegExp(`\\b(?:on\\s+)?(?:the\\s+)?${ORD}\\s+${DOW}\\s+(?:of|in)\\s+(?:the|each|every)\\s+month\\b`, 'i'),
      new RegExp(`\\b(?:monthly|every\\s+month)\\s*,?\\s*(?:on\\s+)?(?:the\\s+)?${ORD}\\s+${DOW}\\b`, 'i'),
      new RegExp(`\\b(?:on\\s+)?(?:the\\s+)?${ORD}\\s+${DOW}\\s*,?\\s*(?:monthly|every\\s+month)\\b`, 'i'),
    ]
    const ORD_VALUE: Record<string, MonthWeek> = { first: 1, '1st': 1, second: 2, '2nd': 2, third: 3, '3rd': 3, fourth: 4, '4th': 4, last: -1 }
    for (const regex of positionPatterns) {
      const match = normalized.match(regex)
      if (!match || match.index === undefined) continue
      const word = match[2].toLowerCase()
      const dayOfWeek: MonthDayOfWeek = word === 'weekend'
        ? 'weekend'
        : (['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const)[DAY_NAMES[word.slice(0, 3)]]
      recurrence = { type: 'monthly', weekOfMonth: ORD_VALUE[match[1].toLowerCase()], dayOfWeek }
      extractedRanges.push({
        start: match.index,
        end: match.index + match[0].length,
        type: 'day-pattern',
        text: getRecurrenceDisplay(recurrence),
      })
      recurrenceFound = true
      break
    }
  }

  for (const { regex, result } of recurrenceFound ? [] : recurrencePatterns) {
    const match = normalized.match(regex)
    if (match && match.index !== undefined) {
      recurrence = result
      extractedRanges.push({
        start: match.index,
        end: match.index + match[0].length,
        type: 'day-pattern',
        text: getRecurrenceDisplay(result),
      })
      recurrenceFound = true
      break
    }
  }

  // Check for "every other [day]" pattern (e.g., "every other monday")
  if (!recurrenceFound) {
    const everyOtherDayRegex = /\bevery\s+other\s+((?:sun|mon|tues?|wed(?:nes)?|thurs?|fri|sat)(?:day)?)\b/i
    const match = normalized.match(everyOtherDayRegex)
    if (match && match.index !== undefined) {
      const dayText = match[1].toLowerCase()
      let dayNum: number | null = null

      for (const [name, num] of Object.entries(DAY_NAMES)) {
        if (dayText.includes(name)) {
          dayNum = num
          break
        }
      }

      if (dayNum !== null) {
        recurrence = { type: 'biweekly', days: [dayNum] }
        extractedRanges.push({
          start: match.index,
          end: match.index + match[0].length,
          type: 'day-pattern',
          text: `EVERY OTHER ${['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'][dayNum]}`,
        })
        recurrenceFound = true
      }
    }
  }

  // Check for specific days: "every monday", "every monday and wednesday"
  if (!recurrenceFound) {
    // Match "every <day>" or "every <day> and <day>"
    const everyDayRegex = /\bevery\s+((?:(?:sun|mon|tues?|wed(?:nes)?|thurs?|fri|sat)(?:day)?(?:\s*(?:,|and)\s*)?)+)\b/i
    const match = normalized.match(everyDayRegex)
    if (match && match.index !== undefined) {
      const daysText = match[1].toLowerCase()
      const days: number[] = []

      for (const [name, num] of Object.entries(DAY_NAMES)) {
        if (daysText.includes(name)) {
          if (!days.includes(num)) {
            days.push(num)
          }
        }
      }

      if (days.length > 0) {
        days.sort((a, b) => a - b)
        recurrence = { type: 'weekly', days }
        extractedRanges.push({
          start: match.index,
          end: match.index + match[0].length,
          type: 'day-pattern',
          text: getRecurrenceDisplay(recurrence),
        })
        recurrenceFound = true
      }
    }
  }

  // Day mappings for parsing day names (including plurals)
  const dayMappings: Array<[RegExp, number]> = [
    [/\bsun(?:day)?s?\b/, 0],
    [/\bmon(?:day)?s?\b/, 1],
    [/\btues?(?:day)?s?\b/, 2],
    [/\bwed(?:nes)?(?:day)?s?\b/, 3],
    [/\bthurs?(?:day)?s?\b/, 4],
    [/\bfri(?:day)?s?\b/, 5],
    [/\bsat(?:urday)?s?\b/, 6],
  ]

  // Check for "on [days]" pattern: "gym on monday", "on tuesdays take out trash"
  if (!recurrenceFound) {
    const onDaysRegex = /\bon\s+((?:(?:sun|mon|tues?|wed(?:nes)?|thurs?|fri|sat)(?:(?:ur)?day)?s?(?:\s*(?:,|and|&)\s*)?)+)\b/i
    const onDaysMatch = normalized.match(onDaysRegex)
    if (onDaysMatch && onDaysMatch.index !== undefined) {
      const daysText = onDaysMatch[1].toLowerCase()
      const days: number[] = []

      for (const [pattern, dayNum] of dayMappings) {
        if (pattern.test(daysText) && !days.includes(dayNum)) {
          days.push(dayNum)
        }
      }

      if (days.length > 0) {
        days.sort((a, b) => a - b)
        recurrence = { type: 'weekly', days }
        recurrenceFound = true
        extractedRanges.push({
          start: onDaysMatch.index,
          end: onDaysMatch.index + onDaysMatch[0].length,
          type: 'day-pattern',
          text: getRecurrenceDisplay(recurrence),
        })
      }
    }
  }

  // Check for monthly patterns with day numbers: "monthly on the 10th", "every month on the 15th"
  if (!recurrenceFound || (recurrence.type === 'monthly' && !recurrence.weekOfMonth)) {
    const monthlyDayPatterns = [
      // "monthly on the 10th", "every month on the 15th"
      /\b(?:monthly|every\s+month)\s+on\s+the\s+(\d{1,2})(?:st|nd|rd|th)?\b/i,
      // "on the 10th of the month", "on the 15th of each month"
      /\bon\s+the\s+(\d{1,2})(?:st|nd|rd|th)?\s+(?:of\s+(?:the|each)\s+month)\b/i,
      // "the 10th of every month"
      /\bthe\s+(\d{1,2})(?:st|nd|rd|th)?\s+of\s+every\s+month\b/i,
    ]

    for (const pattern of monthlyDayPatterns) {
      const match = normalized.match(pattern)
      if (match && match.index !== undefined) {
        const dayNum = parseInt(match[1], 10)
        if (dayNum >= 1 && dayNum <= 31) {
          recurrence = { type: 'monthly', dayOfMonth: dayNum }

          // Remove any existing monthly range (to avoid "MONTHLY MONTHLY (10TH)")
          if (recurrence.type === 'monthly' && recurrenceFound) {
            const monthlyRangeIndex = extractedRanges.findIndex(r => r.type === 'day-pattern')
            if (monthlyRangeIndex !== -1) {
              extractedRanges.splice(monthlyRangeIndex, 1)
            }
          }

          extractedRanges.push({
            start: match.index,
            end: match.index + match[0].length,
            type: 'day-pattern',
            text: getRecurrenceDisplay(recurrence),
          })
          recurrenceFound = true
          break
        }
      }
    }
  }

  // Check for standalone days without "every" or "on" prefix
  // "monday wednesday gym" or "gym monday wednesday" or "trash tuesdays"
  if (!recurrenceFound) {
    const standaloneDaysRegex = /\b((?:(?:sun|mon|tues?|wed(?:nes)?|thurs?|fri|sat)(?:(?:ur)?day)?s?(?:\s*(?:,|and|&)\s*)?)+)\b/gi
    const dayMatches = [...normalized.matchAll(standaloneDaysRegex)]

    if (dayMatches.length > 0) {
      const allDaysText = dayMatches.map(m => m[1]).join(' ').toLowerCase()
      const days: number[] = []

      for (const [pattern, dayNum] of dayMappings) {
        if (pattern.test(allDaysText) && !days.includes(dayNum)) {
          days.push(dayNum)
        }
      }

      if (days.length > 0) {
        days.sort((a, b) => a - b)
        recurrence = { type: 'weekly', days }
        recurrenceFound = true

        // Mark these ranges as extracted for token building
        for (const match of dayMatches) {
          if (match.index !== undefined) {
            extractedRanges.push({
              start: match.index,
              end: match.index + match[0].length,
              type: 'day-pattern',
              text: getRecurrenceDisplay(recurrence),
            })
          }
        }
      }
    }
  }

  // 3. Extract time of day
  for (const tod of TIME_OF_DAY_KEYWORDS) {
    const regex = new RegExp(`\\b${tod}\\b`, 'i')
    const match = normalized.match(regex)
    if (match && match.index !== undefined) {
      // Don't extract if it's already part of the time extraction (rare)
      const alreadyExtracted = extractedRanges.some(
        r => match.index! >= r.start && match.index! < r.end
      )
      if (!alreadyExtracted) {
        timeOfDay = tod as ParsedRoutine['timeOfDay']
        extractedRanges.push({
          start: match.index,
          end: match.index + match[0].length,
          type: 'time-of-day',
          text: tod.toUpperCase(),
        })
        break
      }
    }
  }

  // 4. Extract assignee (first word(s) matching a contact)
  if (contacts.length > 0) {
    // Sort contacts by name length (descending) to match longer names first
    const sortedContacts = [...contacts].sort((a, b) => b.name.length - a.name.length)

    for (const contact of sortedContacts) {
      const contactNameLower = contact.name.toLowerCase()
      // Check if input starts with contact name (case-insensitive)
      if (normalized.startsWith(contactNameLower + ' ') || normalized === contactNameLower) {
        assignee = contact.id
        assigneeName = contact.name
        extractedRanges.push({
          start: 0,
          end: contact.name.length,
          type: 'person',
          text: contact.name.toUpperCase(),
        })
        break
      }
    }
  }

  // 5. Build action string (everything not extracted)
  // Sort ranges by start position
  extractedRanges.sort((a, b) => a.start - b.start)

  const actionParts: string[] = []
  let lastEnd = 0

  for (const range of extractedRanges) {
    if (range.start > lastEnd) {
      actionParts.push(raw.slice(lastEnd, range.start))
    }
    lastEnd = range.end
  }
  if (lastEnd < raw.length) {
    actionParts.push(raw.slice(lastEnd))
  }

  action = actionParts.join(' ')
    .replace(/\s+/g, ' ')           // Normalize whitespace
    .replace(/^[-–—]\s*/, '')       // Remove leading dashes
    .replace(/^\s*at\s*/i, '')      // Remove leading "at"
    .replace(/^\s*on\s*/i, '')      // Remove leading "on"
    .replace(/^\s*every\s*/i, '')   // Remove orphaned "every"
    .replace(/\s+at\s*$/i, '')      // Remove trailing "at"
    .replace(/\s+on\s*$/i, '')      // Remove trailing "on"
    .replace(/\s+and\s*$/i, '')     // Remove trailing "and"
    .trim()

  // 6. Build tokens array for display
  lastEnd = 0
  for (const range of extractedRanges) {
    // Add any text before this range as action/plain
    if (range.start > lastEnd) {
      const betweenText = raw.slice(lastEnd, range.start)
      if (betweenText.trim()) {
        tokens.push({ text: betweenText.trim(), type: 'action' })
      }
    }
    tokens.push({ text: range.text, type: range.type })
    lastEnd = range.end
  }
  // Add any remaining text
  if (lastEnd < raw.length) {
    const remainingText = raw.slice(lastEnd)
    if (remainingText.trim()) {
      tokens.push({ text: remainingText.trim(), type: 'action' })
    }
  }

  return {
    raw,
    assignee,
    assigneeName,
    action,
    recurrence,
    timeOfDay,
    time,
    tokens,
  }
}

/**
 * Format time for display (compact format like "7a", "2:30p")
 */
function formatTimeDisplay(time: string): string {
  const [hours, minutes] = time.split(':').map(Number)
  const h12 = hours % 12 || 12
  const meridiem = hours >= 12 ? 'p' : 'a'
  if (minutes === 0) {
    return `${h12}${meridiem}`
  }
  return `${h12}:${minutes.toString().padStart(2, '0')}${meridiem}`
}

/**
 * Get display text for recurrence pattern
 */
function getRecurrenceDisplay(recurrence: ParsedRoutine['recurrence']): string {
  switch (recurrence.type) {
    case 'daily':
      if (recurrence.interval === 2) return 'EVERY OTHER DAY'
      if (recurrence.interval && recurrence.interval > 2) return `EVERY ${recurrence.interval} DAYS`
      return 'DAILY'
    case 'weekdays':
      return 'WEEKDAYS'
    case 'weekend':
      return 'WEEKEND · EITHER DAY'
    case 'weekends':
      return 'WEEKENDS'
    case 'biweekly': {
      const days = recurrence.days || []
      const dayNames = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']
      if (days.length === 1) {
        return `EVERY OTHER ${dayNames[days[0]]}`
      }
      return 'BIWEEKLY'
    }
    case 'weekly': {
      const days = recurrence.days || []
      const dayNames = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']
      if (days.length === 1) {
        return dayNames[days[0]]
      }
      return days.map(d => dayNames[d]).join(', ')
    }
    case 'monthly':
      if (recurrence.weekOfMonth && recurrence.dayOfWeek) {
        const ord = { '1': 'FIRST', '2': 'SECOND', '3': 'THIRD', '4': 'FOURTH', '-1': 'LAST' }[String(recurrence.weekOfMonth)]
        const day = recurrence.dayOfWeek === 'weekend' ? 'WEEKEND' : recurrence.dayOfWeek.toUpperCase()
        return `MONTHLY (${ord} ${day})`
      }
      if (recurrence.dayOfMonth) {
        const day = recurrence.dayOfMonth
        const suffix = day === 1 ? 'ST' : day === 2 ? 'ND' : day === 3 ? 'RD' : day === 21 ? 'ST' : day === 22 ? 'ND' : day === 23 ? 'RD' : day === 31 ? 'ST' : 'TH'
        return `MONTHLY (${day}${suffix})`
      }
      return 'MONTHLY'
    case 'quarterly':
      return 'QUARTERLY'
    case 'yearly':
      return 'YEARLY'
    default:
      return 'DAILY'
  }
}

type DbRecurrence = {
  type: string; days?: string[]; interval?: number; start_date?: string; day_of_month?: number
  week_of_month?: MonthWeek; day_of_week?: MonthDayOfWeek
}

/**
 * Convert parsed routine to database format
 */
export function parsedRoutineToDb(parsed: ParsedRoutine): {
  name: string
  recurrence_pattern: DbRecurrence
  time_of_day: string | null
  default_assignee: string | null
  raw_input: string
} {
  // Convert recurrence to DB format
  const dayMap = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']
  let dbRecurrence: DbRecurrence
  const today = new Date().toISOString().split('T')[0] // YYYY-MM-DD

  switch (parsed.recurrence.type) {
    case 'daily':
      if (parsed.recurrence.interval && parsed.recurrence.interval > 1) {
        dbRecurrence = { type: 'daily', interval: parsed.recurrence.interval, start_date: today }
      } else {
        dbRecurrence = { type: 'daily' }
      }
      break
    case 'weekdays':
      dbRecurrence = { type: 'weekly', days: ['mon', 'tue', 'wed', 'thu', 'fri'] }
      break
    case 'weekend':
      // One commitment across the window, not one per day.
      dbRecurrence = { type: 'weekend' }
      break
    case 'weekends':
      dbRecurrence = { type: 'weekly', days: ['sat', 'sun'] }
      break
    case 'biweekly': {
      // Store as weekly with interval 2
      const days = parsed.recurrence.days
      if (days && days.length > 0) {
        dbRecurrence = { type: 'weekly', days: days.map(d => dayMap[d]), interval: 2, start_date: today }
      } else {
        dbRecurrence = { type: 'weekly', interval: 2, start_date: today }
      }
      break
    }
    case 'weekly':
      dbRecurrence = {
        type: 'weekly',
        days: (parsed.recurrence.days || []).map(d => dayMap[d])
      }
      break
    case 'monthly':
      dbRecurrence = { type: 'monthly' }
      if (parsed.recurrence.weekOfMonth && parsed.recurrence.dayOfWeek) {
        dbRecurrence.week_of_month = parsed.recurrence.weekOfMonth
        dbRecurrence.day_of_week = parsed.recurrence.dayOfWeek
      } else if (parsed.recurrence.dayOfMonth) {
        dbRecurrence.day_of_month = parsed.recurrence.dayOfMonth
      }
      break
    case 'quarterly':
      dbRecurrence = { type: 'quarterly' }
      break
    case 'yearly':
      dbRecurrence = { type: 'yearly' }
      break
    default:
      dbRecurrence = { type: 'daily' }
  }

  return {
    name: parsed.action,
    recurrence_pattern: dbRecurrence,
    time_of_day: parsed.time,
    default_assignee: parsed.assignee,
    raw_input: parsed.raw,
  }
}

/**
 * Check if a parsed routine is valid (has at least an action)
 */
export function isValidParsedRoutine(parsed: ParsedRoutine): boolean {
  return parsed.action.trim().length > 0
}
